import React, { useEffect, useId, useRef, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button } from '../../../Button/Button'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetFileByIdQuery, useUploadFileRequestMutation, uploadFileWithProgress } from '../../../../store/media/file'
import { useUpdateFileMutation } from '../../../../store/media/media'
import { FileKind, FileStatus } from '../../../../models/File'
import { humanFileSize } from '../../../../common/util'
import { stripImageMetadata } from '../../../../common/fileUtils'
import { contentTypesForProfile, MAX_WORKFLOW_ASSET_BYTES, mediaFileHintOf } from './mediaFileProfiles'
import { MediaFileField } from './MediaFileField'
import { RawReferenceFallback } from './RawReferenceFallback'
import { type ReferenceValueLabelProps, type ZocksReferenceWidgetProps } from './registry'

interface MediaFileUploaderProps extends ZocksReferenceWidgetProps {
  contentTypes: string[]
}

const noopSetFieldUploading = () => {}

const MediaFileUploader: React.FC<MediaFileUploaderProps> = ({
  value,
  onChange,
  disabled,
  label,
  config,
  contentTypes,
  id,
  setFieldUploading = noopSetFieldUploading,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const abortControllerRef = useRef<AbortController | null>(null)
  const uploadKey = useId()
  const uploadingRef = useRef(false)
  const setFieldUploadingRef = useRef(setFieldUploading)
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    setFieldUploadingRef.current = setFieldUploading
    onChangeRef.current = onChange
  }, [setFieldUploading, onChange])
  const [error, setError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()

  const { currentData: fileInfo, isFetching, isError, refetch } = useGetFileByIdQuery(value ? { accountId, fileId: value } : skipToken)

  const uploadFile = async (pickedFile: File) => {
    if (uploadingRef.current) {
      return
    }
    setError(null)

    if (pickedFile.size > MAX_WORKFLOW_ASSET_BYTES) {
      setError(`File is larger than ${humanFileSize(MAX_WORKFLOW_ASSET_BYTES)}.`)
      return
    }

    uploadingRef.current = true
    setIsUploading(true)
    setProgress(0)
    setFieldUploading(uploadKey, true)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const file = await stripImageMetadata(pickedFile)
      const { data } = await triggerUploadFileRequest({
        accountId,
        body: { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.WORKFLOW_ASSET },
      }).unwrap()

      if (abortController.signal.aborted) {
        return
      }

      await uploadFileWithProgress(data.fileUploadUrl, data.fileUploadParameters, file, setProgress, abortController.signal)

      if (abortController.signal.aborted) {
        return
      }

      await triggerUpdateFile({ accountId, fileId: data.fileInfo.fileId, body: { status: FileStatus.UPLOADED } }).unwrap()

      if (abortController.signal.aborted) {
        return
      }

      onChangeRef.current(data.fileInfo.fileId)
    } catch (thrown) {
      const wasAborted = abortController.signal.aborted || (thrown instanceof DOMException && thrown.name === 'AbortError')
      if (!wasAborted) {
        setError('Upload failed. Please try again.')
      }
    } finally {
      abortControllerRef.current = null
      uploadingRef.current = false
      if (!abortController.signal.aborted) {
        setIsUploading(false)
        setFieldUploading(uploadKey, false)
      }
    }
  }

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      setFieldUploadingRef.current(uploadKey, false)
    }
  }, [uploadKey])

  const handleRemove = () => {
    setError(null)
    onChange(undefined)
  }

  const status = isUploading ? (
    <div className='d-flex flex-column gap-2'>
      <span className='paragraph-small'>Uploading… {progress}%</span>
      <ProgressBar now={progress} />
    </div>
  ) : value && !fileInfo && isFetching ? (
    <span className='paragraph-small text-muted'>Loading file…</span>
  ) : value && !fileInfo && isError ? (
    <div className='d-flex align-items-center gap-3'>
      <span className='paragraph-small text-danger'>Couldn't load this file</span>
      <Button type='button' variant='link' disabled={disabled} onClick={() => refetch()}>
        Retry
      </Button>
    </div>
  ) : undefined

  return (
    <MediaFileField
      id={id}
      label={label}
      disabled={disabled}
      accept={contentTypes}
      hint={mediaFileHintOf(config)}
      file={value && fileInfo ? { name: fileInfo.fileName, size: fileInfo.fileSize } : undefined}
      missing={Boolean(value) && !fileInfo}
      status={status}
      error={error}
      onFile={(file) => void uploadFile(file)}
      onReject={() => setError('This file type is not supported.')}
      onRemove={handleRemove}
    />
  )
}

export const MediaFileWidget: React.FC<ZocksReferenceWidgetProps> = (props) => {
  const subType = typeof props.config.subType === 'string' ? props.config.subType : ''
  const contentTypes = contentTypesForProfile(subType)

  if (!contentTypes) {
    const reason = subType ? `Unknown media-file profile "${subType}"` : 'Missing media-file profile'
    return <RawReferenceFallback {...props} reason={reason} />
  }

  return <MediaFileUploader {...props} contentTypes={contentTypes} />
}

export const MediaFileValueLabel: React.FC<ReferenceValueLabelProps> = ({ value, fallback }) => {
  const accountId = useAppSelector(selectAccountId)
  const { currentData } = useGetFileByIdQuery(value ? { accountId, fileId: value } : skipToken)
  return <>{currentData?.fileName ?? fallback}</>
}
